<script setup lang="ts">
import RulesetTable from '@/Components/RulesetTable.vue';
import FeedbackPicker from '@/Components/FeedbackPicker.vue';
import TokenDistribution from '@/Components/TokenDistribution.vue';
import Modal from '@/Components/Modal.vue';
import Loader from '@/Components/Loader.vue';
import Instructions from '@/Components/Instructions.vue';
import OnboardingFlow from '@/Components/OnboardingFlow.vue';
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useParticipantStore, ProbConfAnswer } from '@/Stores/ParticipantStore';
import { usePage } from '@inertiajs/vue3';
import { useRouter } from 'vue-router';
import { makeRuleset, rulesetLabel, studyRulesets, Ruleset } from '@/objects';
import { roundNDigits } from '@/utils';
import { useCopy, renderCopy, blockVisible } from '@/copy';
import SupportButton from '@/Components/SupportButton.vue';

const page = usePage()
const sessionData = page.props.chatSessionData as Record<string, any>
const config = page.props.experimentConfigData as Record<string, any>
const router = useRouter()
const copy = useCopy()

// The Part 2 "Istruzioni" recap, reopenable any time from the button (now block-driven).
const instructionsShown = ref(false)

const pStore = useParticipantStore()

// Part 2 onboarding: a gated instructions-and-comprehension flow shown once when the vote
// sequence begins (the EXPLAIN step onward), before the participant can vote. Blocks whose
// `audience` excludes this treatment are dropped — the same filter feeds the recap. It overlays
// full-screen, so the read is completed before any vote interaction.
const part2OnboardingDone = ref(false)
const part2Blocks = computed(() =>
    ((config?.['onboardingPart2'] ?? []) as any[])
        .filter((block) => blockVisible(block, pStore.state.hasChat))
)
// The two tables a control question can be asked about (see OnboardingFlow's `rulesets`).
const onboardingRulesets = studyRulesets(sessionData)
// The vote's options in display order: ALTERNATIVA on the left, ORIGINALE on the right, the
// same order the Part 2 instructions present them in. The original is whichever ruleset was
// played in Part 1, so the order is decided per session rather than fixed to HG/PD.
const voteOptions = computed(() => {
    const phase1Id = sessionData['phase1Ruleset']?.id
    const all = [sessionData['hgRuleset'], sessionData['pdRuleset']].filter((rs) => rs)

    return [...all.filter((rs) => rs.id !== phase1Id), ...all.filter((rs) => rs.id === phase1Id)]
})

const showPart2Onboarding = computed(() =>
    part2Blocks.value.length > 0
    && phase.value >= EXPLAIN
    && !part2OnboardingDone.value
)

// Step order: rate the consultant first (chat treatments only), then a short explainer of
// the upcoming vote, then the ruleset vote, the belief prediction, and finally the waiting
// screen that submits everything. The belief is elicited either as a point estimate
// (PROBABILITY then CONFIDENCE) or as a token DISTRIBUTION, depending on the session mode.
const RATING = 0
const EXPLAIN = 1
const RULESET = 2
// Two one-off explanation pages shown once (point mode only) between the ruleset vote and the
// first probability question: what the prediction task is, and how it's paid.
const PRED_INTRO = 3
const PRED_PAYMENT = 4
const PROBABILITY = 5
const CONFIDENCE = 6
const DISTRIBUTION = 7
const WAITING = 8

// Session-configured belief elicitation. In distribution mode the PROBABILITY/CONFIDENCE
// steps are replaced by a single DISTRIBUTION step per ruleset.
const isDistribution = sessionData['predictionMode'] === 'distribution'

// The scoring-rule explanation opens in a dismissable modal from the "qui" link.
// `ruleModalShown` is the distribution-mode rule; `paymentRuleShown` is the point-mode payment
// rule shown from the PRED_PAYMENT explanation page.
const ruleModalShown = ref(false)
const paymentRuleShown = ref(false)

// The distribution phase is its own scroll container; reset to the top when swapping to the
// second table so the new page starts from the title, not wherever the previous one was left.
const distScrollEl = ref<HTMLElement | null>(null)
// No-help treatments have no consultant to rate, so they start on the explainer.
const phase = ref(pStore.state.hasChat ? RATING : EXPLAIN)
const answers = reactive({
    table: 0,
    rating: null,
    predictions: Array<ProbConfAnswer>(0),
    // Time spent on the table-vote step, from the moment it appears to "Conferma" (ms). Lives
    // only here until the WAITING step ships it with the rest, like every other answer: a
    // reload mid-sequence restarts the vote, and the clock with it.
    voteDurationMs: null as number | null,
})
// performance.now() at the moment the RULESET step is shown (see the `phase` watcher).
let rulesetShownAt: number | null = null
const nextPrediction = ref(getPCA("pd"))

const probA = ref<number|null>(null)

// Probability of action B mirrors A (they sum to 100%). Shown read-only; blank until A is
// a valid percentage so the participant isn't nudged by a pre-filled value.
const probB = computed(() => {
    const a = Number(nextPrediction.value.prob)
    if (nextPrediction.value.prob === null || nextPrediction.value.prob as unknown === '' || isNaN(a) || a < 0 || a > 100) {
        return ''
    }

    return 100 - a
})

// Confidence shown as a whole percentage; null until the participant first moves the slider.
const confidencePct = computed(() => {
    if (nextPrediction.value.conf < 0) {
        return null
    }
    return Math.round(nextPrediction.value.conf * 100)
})

// The confidence question is hardcoded (no longer editable copy) and reflects the probability the
// participant just entered on the previous screen. The payoff table is shown above it, so the text
// refers to it as "qui sopra". The ±1% interval is clamped to [0, 100], so the endpoints read
// "fra il 0% e il 1%" / "fra il 99% e il 100%" when they answered 0% / 100%.
const confidencePrompt = computed(() => {
    const p = Math.round(Number(nextPrediction.value.prob ?? 0))
    const low = Math.max(0, p - 1)
    const high = Math.min(100, p + 1)
    return `Hai indicato che c’è una probabilità del ${p}% che un partecipante selezionato casualmente in un turno casuale con la tabella dei guadagni qui sopra scelga l'azione A. Quanto sei sicuro che la probabilità reale sia fra il ${low}% e il ${high}%?`
})

const title = computed(() => {
    switch (phase.value) {
        case RATING:
            return "Valuta il consulente che ti ha assistito"
        case EXPLAIN:
            return "Stai per votare"
        case RULESET:
            return "Vota per la tabella che preferiresti usare d'ora in avanti"
        // PRED_INTRO / PRED_PAYMENT carry their own heading inside the editable copy
        // (like the onboarding blocks), so they return no separate title here.
        case DISTRIBUTION: {
            // Beliefs are elicited one table at a time; name the table being predicted from the
            // table itself. The step order is fixed (PD then HG) but which of the two is the
            // "original" is not: the Part 1 game is configurable per session.
            const label = rulesetLabel(nextPrediction.value.ruleset?.id, sessionData['phase1Ruleset']?.id)

            return label ? `Previsione tabella ${label.toLowerCase()}` : "Previsione"
        }
        case WAITING:
            return "Attendi che tutti i partecipanti abbiano votato"
    }

    return ""
})
const answeredCurrent = computed(() => {
    switch (phase.value) {
        case RATING:
            return answers.rating
        case EXPLAIN:
            return true
        case PRED_INTRO:
            return true
        case PRED_PAYMENT:
            return true
        case RULESET:
            return answers.table
        case PROBABILITY:
            return probB.value !== ''
        case CONFIDENCE:
            return nextPrediction.value.conf >= 0
        case DISTRIBUTION:
            return nextPrediction.value.distribution.reduce((a, b) => a + b, 0) === 100
    }

    return false
})

// The first belief step after the ruleset choice, and the step that completes one ruleset's
// belief, both differ by mode: DISTRIBUTION is a single step, point mode splits into
// PROBABILITY then CONFIDENCE.
const firstBeliefPhase = isDistribution ? DISTRIBUTION : PROBABILITY

function toNext() {
    if (phase.value === RULESET) {
        if (rulesetShownAt !== null) {
            answers.voteDurationMs = Math.round(performance.now() - rulesetShownAt)
        }
        // Point mode shows the two one-off explanation pages before the first probability
        // question; distribution mode goes straight to its single belief step. The second table
        // (see below) skips the explanations and reuses `firstBeliefPhase`.
        phase.value = isDistribution ? DISTRIBUTION : PRED_INTRO
        return
    }

    const beliefComplete = phase.value === (isDistribution ? DISTRIBUTION : CONFIDENCE)
    if (!beliefComplete) {
        phase.value += 1
        return
    }

    answers.predictions.push({
        ruleset: nextPrediction.value.ruleset,
        prob: nextPrediction.value.prob,
        conf: nextPrediction.value.conf,
        distribution: nextPrediction.value.distribution,
    })

    if (answers.predictions.length >= 2) {
        phase.value = WAITING
        return
    }

    probA.value = null
    nextPrediction.value = getPCA("hg")
    phase.value = firstBeliefPhase

    // Same DISTRIBUTION element is reused for the second table, so its scroll position carries
    // over — send the new page back to the top.
    if (isDistribution) {
        nextTick(() => distScrollEl.value?.scrollTo({ top: 0 }))
    }
}

// The number input's min/max don't validate values while being typed, so clamp to 0–100 and
// round ourselves — and write the result back into the field so it can never display a value
// outside the range.
watch(probA, (v) => {
    if (v === null || (v as unknown) === '') {
        nextPrediction.value.prob = null
        return
    }

    const n = Number(v)
    if (isNaN(n)) {
        return
    }

    const clamped = roundNDigits(Math.min(100, Math.max(0, n)), 2)
    nextPrediction.value.prob = clamped

    // Reflect the clamp in the input. Re-runs this watch once, then converges (v === clamped).
    if (clamped !== v) {
        probA.value = clamped
    }
})

watch(() => nextPrediction.value.conf, () => {
    const sliders = document.querySelectorAll("input[type='range']")

    sliders.forEach((el: HTMLInputElement) => {
        const min = parseFloat(el.min) || 0;
        const max = parseFloat(el.max) || 100;
        const pct = (parseFloat(el.value) - min) / (max - min) * 100;
        el.style.setProperty('--range-pct', pct + '%');
    })
})

watch(phase, () => {
    // The Part 2 gated onboarding surfaces itself via `showPart2Onboarding` once the vote
    // sequence reaches EXPLAIN; no need to auto-open the recap here.
    if (phase.value === RULESET) {
        // The onboarding overlay blocks EXPLAIN's button, so by the time the step is reached
        // the tables are actually on screen: start the vote clock here.
        rulesetShownAt = performance.now()
        return
    }

    if (phase.value !== WAITING) {
        return
    }

    pStore.sendVote(
        answers.predictions,
        answers.table,
        answers.rating,
        answers.voteDurationMs
    )
})

function getPCA(rsName: string): ProbConfAnswer {
    return {
        ruleset: makeRuleset(sessionData[`${rsName}Ruleset`]),
        // Probability of action A, entered as a whole percentage (0–100). Left null so the
        // field starts empty and doesn't anchor the participant to a default value.
        prob: null as number | null,
        // Confidence in that prediction, kept as a 0–1 fraction (slider). -1 until the
        // participant moves it, so we can require a deliberate answer.
        conf: -1,
        // Distribution mode only: 100 tokens over 10 probability bins, all starting empty.
        distribution: Array(10).fill(0),
    }
}

onMounted(() => {
    nextPrediction.value = getPCA("pd")

    // The Part 2 gated onboarding shows itself from EXPLAIN onward (see showPart2Onboarding):
    // no-chat treatments start on EXPLAIN and see it immediately; chat treatments reach it once
    // the consultant has been rated.

    pStore.onVoteDone = (rs) => {
        router.push({ name: "Participant", replace: true })
    }
})
</script>

<template>
    <Instructions v-model="instructionsShown" :part="2" :has-chat="pStore.state.hasChat" :chat-type="pStore.state.chatType" />
    <OnboardingFlow v-if="showPart2Onboarding" :parts="part2Blocks" :chat-type="pStore.state.chatType"
        :rulesets="onboardingRulesets" @done="part2OnboardingDone = true" />
    <!-- Both buttons sit in the topbar flow and wrap when they don't fit side by side, rather
         than one floating below the bar over the page content. -->
    <Teleport to="#topbar-left">
        <div class="row flex-wrap items-center gap-2">
            <button @click="instructionsShown = true" class="btn-primary px-4! py-2!">
                Istruzioni
            </button>
            <SupportButton></SupportButton>
        </div>
    </Teleport>
    <!-- Every step is its own scroll column, with the heading and the confirm button in normal
         flow: on a short (mobile) viewport the page scrolls instead of stacking the content
         under an absolutely-placed title or a floating button. -->
    <div v-if="phase === RULESET" data-testid="vote-step-ruleset" class="phase">
        <div class="phase-block col gap-2">
            <p class="phase-title">{{ title }}</p>
            <p>Clicca sulla tabella che vuoi selezionare.</p>
        </div>
        <!-- `.ruleset-pair` (app.css) keeps the two side by side on every screen width, in the
             same order as the Part 2 instructions: alternative left, original right. Its gap
             also clears the selected table's outline, which sits 10px out. -->
        <div class="phase-block ruleset-pair">
            <!-- Each table is captioned ORIGINALE / ALTERNATIVA, the same names the Part 2
                 instructions use, so the vote doesn't ask to pick between two unnamed grids.
                 The caption sits inside the clickable (and dimmable) wrapper, and `gap-4`
                 keeps it clear of the selected table's outline. -->
            <div v-for="rs in voteOptions" :key="rs.id"
                class="vote-option col items-center gap-4"
                :class="{'opacity-20': answers.table !== rs.id && answers.table > 0}"
                @click="() => answers.table = (answers.table === rs.id) ? 0 : rs.id"
            >
                <span v-if="rulesetLabel(rs.id, sessionData['phase1Ruleset']?.id)" class="font-bold">
                    TABELLA {{ rulesetLabel(rs.id, sessionData['phase1Ruleset']?.id) }}
                </span>
                <RulesetTable :id="`rs-vote-${rs.id}`" :ruleset="makeRuleset(rs)"
                    :selected="answers.table === rs.id" class="w-full"
                />
            </div>
        </div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Conferma
        </button>
    </div>
    <div v-else-if="phase === RATING" data-testid="vote-step-rating" class="phase">
        <p class="phase-title">{{ title }}</p>
        <FeedbackPicker class="phase-block" v-model="answers.rating" />
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Conferma
        </button>
    </div>
    <div v-else-if="phase === EXPLAIN" data-testid="vote-step-explain" class="phase">
        <p class="phase-title">{{ title }}</p>
        <div class="copy-html phase-block" v-html="renderCopy(copy('vote_explainer'))"></div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Vai alla votazione
        </button>
    </div>
    <!-- The two prediction-intro pages carry their heading inside the editable copy, so they
         have no separate title. -->
    <div v-else-if="phase === PRED_INTRO" data-testid="vote-step-pred-intro" class="phase">
        <div class="copy-html phase-block" v-html="renderCopy(copy('vote_prediction_intro'))"></div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Avanti
        </button>
    </div>
    <div v-else-if="phase === PRED_PAYMENT" data-testid="vote-step-pred-payment" class="phase">
        <div class="copy-html phase-block" v-html="renderCopy(copy('vote_payment_intro'))"></div>
        <p class="phase-block">
            Se sei interessato alla regola che determina il compenso in funzione del grado di
            accuratezza della previsione,
            <button type="button" @click="paymentRuleShown = true" class="text-link">clicca qui</button>.
        </p>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Avanti
        </button>
        <Modal v-model="paymentRuleShown" :close-outside="true" title="Regola di compenso"
            action-label="Ho capito" width="w-140">
            <div class="copy-html leading-relaxed" v-html="renderCopy(copy('vote_payment_rule'))"></div>
        </Modal>
    </div>
    <div v-else-if="phase === PROBABILITY" data-testid="vote-step-probability" class="phase">
        <p class="phase-block">Con quale probabilità pensi che un partecipante selezionato casualmente, in un turno casuale, sceglierebbe l'azione A giocando con la tabella dei guadagni qui sotto?</p>
        <div class="phase-block">
            <RulesetTable :ruleset="nextPrediction.ruleset" class="pred-table"/>
        </div>
        <p class="phase-block">0 significa che ritieni impossibile che il partecipante scelga A, 50 significa che ti aspetti che il partecipante scelga A o B con uguali probabilità, e 100 significa che sei assolutamente certo che il partecipante scelga A.</p>
        <div class="phase-block row flex-wrap gap-8 items-start">
            <label class="col gap-2">
                <span class="text-sm max-w-45">Probabilità azione A</span>
                <div class="row items-center gap-1.5">
                    <input type="number" min="0" max="100" step="1" inputmode="numeric"
                        class="prob-input" v-model.number="probA">
                    <span>%</span>
                </div>
            </label>
            <div v-if="probB !== ''" class="col gap-2">
                <span class="text-sm max-w-45">Probabilità azione B</span>
                <div class="row items-center gap-1.5">
                    <span class="prob-readonly">{{ probB }}</span>
                    <span>%</span>
                </div>
            </div>
        </div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Conferma
        </button>
    </div>
    <div v-else-if="phase === CONFIDENCE" data-testid="vote-step-confidence" class="phase">
        <div class="phase-block">
            <RulesetTable :ruleset="nextPrediction.ruleset" class="pred-table"/>
        </div>
        <p class="phase-block">{{ confidencePrompt }}</p>
        <div class="phase-block col items-stretch gap-2">
            <div class="text-center min-h-7 flex items-center justify-center">
                <span v-if="confidencePct === null" class="text-sm text-gray-500">Clicca sulla barra per indicare il tuo grado di certezza</span>
                <span v-else class="text-lg font-semibold">{{ confidencePct + '%' }}</span>
            </div>
            <input class="generator-input" :class="{ 'thumb-hidden': confidencePct === null }" type="range" min="0" max="1" step="0.05" v-model.number="nextPrediction.conf">
            <!-- Each stack is pinned to its end of the bar and aligned outwards, so the number
                 sits on the extremity with its label under it. No ±½-width translate: on a
                 narrow screen that pushed the labels outside the column and scrolled the page. -->
            <div class="row justify-between items-start w-full gap-3 text-sm text-gray-600 leading-tight">
                <div class="col items-start text-left">
                    <span>0</span>
                    <span>molto incerto</span>
                </div>
                <div class="col items-end text-right">
                    <span>100</span>
                    <span>completamente certo</span>
                </div>
            </div>
        </div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Conferma
        </button>
    </div>
    <div v-else-if="phase === DISTRIBUTION" ref="distScrollEl" data-testid="vote-step-distribution" class="phase">
        <p class="phase-title">{{ title }}</p>
        <div class="phase-block col items-start gap-5">
            <div class="copy-html" v-html="renderCopy(copy('vote_distribution_prompt'))"></div>
            <RulesetTable :ruleset="nextPrediction.ruleset" class="pred-table"/>
            <div class="copy-html" v-html="renderCopy(copy('vote_distribution_tokens'))"></div>
            <p>
                La regola è spiegata in dettaglio
                <button type="button" @click="ruleModalShown = true" class="text-link">qui</button>.
            </p>
            <!-- The question again, right where it gets answered: by the time the histogram is
                 on screen the question and its payoff table have scrolled up out of view. -->
            <div class="copy-html" v-html="renderCopy(copy('vote_distribution_reminder'))"></div>
            <TokenDistribution v-model="nextPrediction.distribution" />
        </div>
        <button :disabled="!answeredCurrent" @click="toNext" class="btn-primary phase-next">
            Conferma
        </button>
        <Modal v-model="ruleModalShown" :close-outside="true" title="Regola di compenso"
            action-label="Ho capito" width="w-140">
            <p class="leading-relaxed">
                Data la distribuzione elicitata p={p_1,...,p_j,...}, chiamando l'intervallo j* quello vero
                (cioè quello in cui cade la frequenza rilevata di cooperatori nel gruppo) vinci TOT euro con probabilità
                <img src="/assets/formula.png" alt="π(p, j*) = (1 + 2·p_j* − Σ p_j²) / 2" class="inline-block align-middle h-10 mx-1.5">
                , zero altrimenti.
            </p>
        </Modal>
    </div>
    <div v-else-if="phase === WAITING" data-testid="vote-step-waiting" class="phase justify-center">
        <!-- The dots are the sentence's ellipsis, so they sit on the title's own line rather
             than under it. `phase-title` is deliberately not used here: it spans the full
             column width, which would push the loader onto the next row. -->
        <div class="phase-block row flex-wrap items-center gap-2">
            <p class="text-lg">{{ title }}</p>
            <Loader :loading="true" class="w-12.5"/>
        </div>
    </div>
</template>

<style scoped>
@reference 'tailwindcss';
@reference '#app.css';

.side-container {
    @apply p-3 bg-pink-650;
}

input {
    /* Nuke the default browser style */
    -webkit-appearance: none;
    appearance: none;
    @apply appearance-none m-0;
}

input[type="radio"] {
    @apply size-4.5 border border-blue-700 rounded-full;

    @variant checked {
        @apply bg-blue-700;
    }
}

label {
    @apply text-sm;
}

/* Every vote step shares this shell: a full-height scroll column holding the heading, the
   content and the confirm button, all in flow. Nothing is absolutely placed, so a viewport too
   short for the content scrolls rather than overlapping it. */
.phase {
    @apply col items-center w-full h-full overflow-y-auto gap-6 px-5 pt-6 pb-10 text-md text-left;
}

/* Headings, prose and the continue button all take the reading column's full width, so they
   share one left edge and the button spans exactly as far as the text above it. */
.phase-title,
.phase-block,
.phase-next {
    @apply w-full max-w-240;
}

/* Same reading treatment as the onboarding overlay (OnboardingFlow.vue): the researcher-authored
   copy is justified, while headings, lists and the copy's own centered captions are left alone. */
.copy-html :deep(p) {
    text-align: justify;
}

.phase-title {
    @apply text-lg;
}

.phase-next {
    @apply text-md! p-4!;
}

/* The payoff table the belief questions are about (probability, confidence, distribution). It
   grows on a wide screen like the vote's tables, but a step less: here it is the reference the
   question points at, not the thing being chosen. Same story at the bottom of the clamps — below
   ~800px it is exactly the size it has always been. */
.pred-table {
    width: clamp(256px, 32vw, 360px);
    font-size: clamp(var(--text-sm), 1.8vw, 21px);
}

.pred-table :deep(th) {
    font-size: 0.83em;
}

.pred-table :deep(tr) {
    height: 2em;
}

/* A button sitting inside a sentence has to read as a link, not as a control: the global
   `button` rule (app.css) gives every button its own `text-sm`, line-height and padding, which
   otherwise renders the word visibly smaller than the text around it. Inheriting instead of
   hardcoding a size keeps it right wherever the surrounding copy is scaled. */
.text-link {
    @apply underline cursor-pointer;
    font-size: inherit;
    line-height: inherit;
    padding: 0;
}

.prob-input {
    @apply border border-blue-700 rounded-sm px-3 py-2 w-25 text-center text-md;
}

/* Read-only mirror of probability B: plain text, no input chrome, so it doesn't invite
   interaction. Padded to roughly match the A input's height for alignment. */
.prob-readonly {
    @apply px-3 py-2 w-25 text-center text-md font-semibold;
}

/**
    Sliders style generated with https://www.cssportal.com/style-input-range/
    and then tailwind-fied
*/

/* Target Input */
.generator-input {
  -webkit-appearance: none;
  appearance: none;
  @apply w-full bg-transparent cursor-pointer h-8;
}

/* --- Webkit Browsers --- */
.generator-input::-webkit-slider-runnable-track {
  @apply h-4 rounded-[10px];
  background: linear-gradient(to right, var(--color-blue) var(--range-pct, 0%), var(--color-pink-650) var(--range-pct, 0%));
}

.generator-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  
  @apply size-6 bg-blue rounded-[30px] -mt-2 border-0;
}

/* --- Firefox --- */
.generator-input::-moz-range-track {
  @apply h-4 rounded-[10px];
  background: linear-gradient(to right, var(--color-blue) var(--range-pct, 0%), var(--color-pink-650) var(--range-pct, 0%));
}

.generator-input::-moz-range-thumb {
    @apply size-6 bg-blue rounded-[30px] border-0;
}

/* Before the first interaction (conf < 0) the thumb is hidden so no circle
   suggests a value has already been picked; the helper text prompts the click. */
.generator-input.thumb-hidden::-webkit-slider-thumb {
    opacity: 0;
}

.generator-input.thumb-hidden::-moz-range-thumb {
    opacity: 0;
}
</style>